import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Textfield, Dropdown } from '@workboard/wobo-ui';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { TypeFaceText } from '../../../../inputs/buttons.component';
import HelpTextComp from '../../../../helper/helptext.component';
import { getTargetValue } from '../../../../common/getTargetValue.component';
import * as MetricFunc from '../../../../../utils/metric';
import { krdetailConstants } from '../../../../../constants/krdetails.constants';
import { isNumberWithComma } from '../../../../../utils/function';
import { MetricObj, AddOkrData, CascadeKrItem } from './cascadeKrTypes';

const StyledDropdown = styled(Dropdown)`
  min-width: 113px !important;
  max-width: fit-content;
  height: 39px !important;
  > svg {
    margin-right: 10px !important;
  }
  .MuiSelect-root {
    background-color: #f2f2f2 !important;
    border: 1px solid rgba(0, 0, 0, 0.06);
    height: 38px;
  }
  .MuiSelect-select.Mui-disabled {
    .MuiListItemText-root {
      color: rgba(0, 0, 0, 0.87) !important;
    }
  }
  .MuiSelect-icon.Mui-disabled {
    display: none;
  }
  #unit-measure-dropdown {
    min-width: 177px;
  }
  #cal-overall-dropdown {
    min-width: 226px;
  }
  #select-day-dropdown {
    min-width: 120px;
  }
  #select-interval-dropdown {
    min-width: 113px;
  }
  #result-value-dropdown {
    min-width: 220px;
  }
  #calculate-update-as-dropdown {
    min-width: 224px;
  }
`;

export type CascadePrimaryKrSectionProps = {
  metricObj: MetricObj;
  isError: boolean;
  isStretchEnabled: boolean;
  isOldDis: boolean;
  addOkrData: AddOkrData;
  renderError: (field: string) => React.ReactNode;
  onChange: (updatedMetric: Partial<MetricObj>, clearedErrors?: string[]) => void;
  /** When provided, used to sync Unit of Measurement when selecting Average/Total of Actuals */
  cascadeList?: CascadeKrItem[];
};

const getDefaultMetric = (cascadeList: CascadeKrItem[]) => {
  const childrenWithMetric = cascadeList.filter((c) => c.metric != null);
  if (childrenWithMetric.length > 0) {
    const units = childrenWithMetric.map((c) => Number(c.metric));
    const counts: Record<number, number> = {};
    units.forEach((u) => {
      counts[u] = (counts[u] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted.length ? Number(sorted[0][0]) : units[0];
  }
  return undefined;
};

export const CascadePrimaryKrSection: React.FC<CascadePrimaryKrSectionProps> = ({
  metricObj,
  isError,
  isStretchEnabled,
  isOldDis,
  addOkrData,
  renderError,
  onChange,
  cascadeList = [],
}) => {
  const { t } = useTranslation();
  const isMetricManuallySetRef = useRef(false);

  // Memoized values
  const initialMetricTypeOptions = useMemo(
    () =>
      MetricFunc.getMetricTypeOptions().map((m: { name: string; [key: string]: unknown }) => ({
        ...m,
        name: `${t('Count as')} ${m.name.toLowerCase()}`,
      })),
    [t]
  );

  const newMeasureMetricData =
    metricObj.measure_metric_data != null
      ? Number(metricObj.measure_metric_data)
      : MetricFunc.MEASURE_METRIC_DATA_TOTAL_ACHIEVED_VALUES;

  const isAverageProgressPercent =
    newMeasureMetricData === MetricFunc.MEASURE_METRIC_DATA_AVG_PROGRESS_PERCENTAGE;
  const unitOfMeasurementOptions = isAverageProgressPercent
    ? initialMetricTypeOptions.filter(
      (m) => (m as unknown as { value: number }).value === krdetailConstants.METRIC_PERCENTAGE
    )
    : initialMetricTypeOptions;
  const unitOfMeasurementValue = isAverageProgressPercent
    ? krdetailConstants.METRIC_PERCENTAGE
    : Number(metricObj.metric);

  const isEnhancedCascadesEnabled = window.wbConfig?.isEnhancedCascadesEnabled ?? false;
  const shouldUseNewMode =
    isEnhancedCascadesEnabled ||
    newMeasureMetricData !== MetricFunc.MEASURE_METRIC_DATA_TOTAL;
  const displayValue =
    newMeasureMetricData === MetricFunc.MEASURE_METRIC_DATA_TOTAL && isEnhancedCascadesEnabled
      ? MetricFunc.MEASURE_METRIC_DATA_TOTAL_ACHIEVED_VALUES
      : newMeasureMetricData;

  // Event handlers
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value.trim();
      const fieldName = e.target.name as keyof MetricObj;

      if (typeof metricObj[fieldName] !== 'undefined') {
        const val =
          (v && isNumberWithComma(v)) || v === '' ? v : (metricObj[fieldName] as string);
        onChange({ [fieldName]: val }, [fieldName]);
      }
    },
    [metricObj, onChange]
  );

  const handleTargetDropdownChange = useCallback(
    (_e: React.ChangeEvent<HTMLSelectElement>) => {
      const itemValue = parseInt(_e.target.value, 10);
      if (itemValue) {
        onChange({ counting: itemValue }, ['initial_data', 'target', 'lower_limit', 'upper_limit', 'stretch']);
      }
    },
    [onChange]
  );

  const handleIntervalChange = useCallback(
    (_e: React.ChangeEvent<HTMLSelectElement>) => {
      const itemValue = parseInt(_e.target.value, 10);
      if (itemValue) {
        onChange({ interval: itemValue });
      }
    },
    [onChange]
  );

  const handleWeekDayChange = useCallback(
    (_e: React.ChangeEvent<HTMLSelectElement>) => {
      const itemValue = parseInt(_e.target.value, 10);
      if (itemValue) {
        onChange({ weekly_update_day: itemValue });
      }
    },
    [onChange]
  );

  const handleCascadeUpdateAsChange = useCallback(
    (_e: React.ChangeEvent<HTMLSelectElement>) => {
      const itemValue = parseInt(_e.target.value, 10);
      const updates: Partial<MetricObj> = { measure_metric_data: itemValue };

      if (itemValue === MetricFunc.MEASURE_METRIC_DATA_AVG_PROGRESS_PERCENTAGE) {
        updates.metric = krdetailConstants.METRIC_PERCENTAGE;
      } else if (
        // automatically set the metric if the metric has not been manually set
        !isMetricManuallySetRef.current &&
        (
          itemValue === MetricFunc.MEASURE_METRIC_DATA_TOTAL_ACHIEVED_VALUES ||
          itemValue === MetricFunc.MEASURE_METRIC_DATA_AVG_ACHIEVED_VALUES
        )
      ) {
        const defaultMetric = getDefaultMetric(cascadeList);
        if (defaultMetric) {
          updates.metric = defaultMetric;
        }
      }
      onChange(updates);
    },
    [onChange, cascadeList]
  );

  const handleMetricTypeChange = useCallback(
    (_e: React.ChangeEvent<HTMLSelectElement>) => {
      const itemValue = parseInt(_e.target.value, 10);
      if (itemValue) {
        isMetricManuallySetRef.current = true;
        onChange({ metric: itemValue });
      }
    },
    [onChange]
  );

  return (
    <div className="cascade-kr-info-wrapper">
      <div className="fieldcontent">
        <div className="labeltext-27px">{t('PRIMARY KEY RESULT')}</div>
      </div>
      <div className="fieldcontent">
        <div className="basickrstarttargets">
          <div className="left" style={{ width: '94px', marginRight: '12px' }}>
            <div className="labeltext-25px" id="label-starting_from">
              {t('Starting from')}
              <TypeFaceText key="typeface" />
            </div>
            <div className="">
              <Textfield
                aria-labelledby="label-starting_from"
                data-testid="starting-from"
                className="text-target-field"
                name="initial_data"
                id="initial_data"
                autoComplete="off"
                onChange={handleInputChange}
                type="number"
                value={metricObj.initial_data}
                style={{ width: '84px' }}
                placeholder={t('Enter Value')}
              />
            </div>
            {isError ? renderError('initial_data') : ''}
          </div>
          <div className="left" style={{ marginRight: '12px' }}>
            <div className="krs-target-value-text inLineBlk targetHelp" style={{ height: 24, width: '100%' }}>
              {t('Result value')}
            </div>
            <div>
              <StyledDropdown
                options={getTargetValue(true)}
                onChange={handleTargetDropdownChange}
                id="result-value-dropdown"
                data-testid="result-value-dropdown"
                value={Number(metricObj.counting)}
                aria-label={t('Result Value')}
                showStartNodeOnSelect
                optionTitleProp="name"
                optionValueProp="value"
                disabled={isOldDis}
                MenuProps={{
                  style: {
                    zIndex: '1500000',
                  },
                }}
              />
              <div className="clear" />
            </div>
          </div>
          <div className={!isStretchEnabled ? 'left' : 'left target-cascade'}>
            <div className={!isStretchEnabled ? 'labeltext-25px' : 'target-text-25px'} id="target-input">
              {t('Target value')}
              <TypeFaceText key="typeface" />
            </div>
            {isStretchEnabled && (
              <div className="inLineBlk left">
                <div className="labeltext-25px marginL0">
                  {t('Committed target')}
                  <HelpTextComp popUpClass="leftAlign" wrapperClass="committedKRHelpText marginL3">
                    {t(
                      'When you set a Committed Result, the RAG status will follow a more aggressive curve throughout the quarter, and your progress at the end of the quarter will only be green if you achieve 100% of your target.',
                    ) +
                      t(
                        'This is helpful for financial targets, SLAs, or similar targets that you have a commitment to hit.',
                      )}
                  </HelpTextComp>
                </div>
              </div>
            )}
            <div>
              <Textfield
                aria-labelledby="target-input"
                data-testid="target-value"
                className={`text-target-field${isStretchEnabled ? ' stretch-text-target-width' : ' text-target-width'}`}
                name="target"
                id="target"
                autoComplete="off"
                onChange={handleInputChange}
                type="number"
                value={metricObj.target}
                placeholder={t('Enter Value')}
              />
            </div>
            {isError ? renderError('target') : ''}
          </div>
          {isStretchEnabled && (
            <div className="left target-cascade">
              <div className="labeltext-25px" id="target-input" />
              <div className="inLineBlk left">
                <div className="labeltext-25px marginL0">
                  {t('Stretch target')}
                  <HelpTextComp popUpClass="leftAlign" wrapperClass="committedKRHelpText marginL3">
                    {t(
                      'Aim for best possible! Your stretch target is what you aspire to achieve beyond what has been committed',
                    )}
                  </HelpTextComp>
                </div>
              </div>
              <div>
                <Textfield
                  aria-labelledby="stretch-input"
                  data-testid="target-value"
                  className="text-target-field stretch-text-target-width"
                  name="stretch"
                  id="stretch"
                  autoComplete="off"
                  onChange={handleInputChange}
                  type="number"
                  value={metricObj.stretch}
                  placeholder={t('Enter Value')}
                />
              </div>
              {isError ? renderError('stretch') : ''}
            </div>
          )}
          <div className="clear" />
        </div>
      </div>
      <div className="fieldcontent">
        <div className="left" style={{ marginRight: '26px' }}>
          <div className="labeltext-25px">{t('Update cadence')}</div>
          <div>
            <StyledDropdown
              id="select-interval-dropdown"
              aria-label={t('How often to update?')}
              value={Number(metricObj.interval)}
              onChange={handleIntervalChange}
              required
              autoMenuWidth
              optionTitleProp="name"
              optionValueProp="value"
              disabled={isOldDis}
              options={MetricFunc.getIntervalOptions(
                'default',
                addOkrData.isEnabledBiweeklyKR,
                false,
                Number(metricObj.interval) === krdetailConstants.INTERVAL_BIQUARTERLY,
              )}
              MenuProps={{
                style: {
                  zIndex: '1500000',
                },
              }}
            />
          </div>
        </div>
        {MetricFunc.isWeeklyInterval(metricObj.interval) && (
          <div className="left" style={{ marginRight: '26px' }}>
            <div className="labeltext-25px">{t('On')}</div>
            <div>
              <StyledDropdown
                data-testid="myAlign"
                id="select-day-dropdown"
                aria-label={t('select day to update')}
                value={Number(metricObj.weekly_update_day)}
                autoMenuWidth
                onChange={handleWeekDayChange}
                optionTitleProp="name"
                optionValueProp="value"
                disabled={isOldDis}
                options={MetricFunc.getWeekDaysOptions()}
                MenuProps={{
                  style: {
                    zIndex: '1500000',
                  },
                }}
              />
            </div>
          </div>
        )}
        <div className="clear" />
      </div>

      <div className="fieldcontent">
        <div className="left" style={{ marginRight: '26px' }}>
          <div className="labeltext-25px">
            {t('Calculate update as')}
          </div>
          <div>
            <StyledDropdown
              options={MetricFunc.getCalculationCascadeProgressOption(
                shouldUseNewMode ? 'new' : 'oldCascade',
              )}
              onChange={handleCascadeUpdateAsChange}
              id="calculate-update-as-dropdown"
              data-testid="calculate-update-as-dropdown"
              value={displayValue}
              aria-label={t('Calculate update as')}
              optionTitleProp="name"
              optionValueProp="value"
              MenuProps={{
                style: {
                  zIndex: '1500000',
                },
              }}
              autoMenuWidth
            />
            <div className="clear" />
          </div>
        </div>

        <div className="left">
          <div className="labeltext-25px" id="label-unit-measurement">
            {t('Unit of measurement')}
            <TypeFaceText key="typeface" />
          </div>
          <div>
            <StyledDropdown
              aria-labelledby="label-unit-measurement"
              data-testid="unit-measurement-dropdown"
              id="unit-measurement-dropdown"
              aria-label={t('Unit of measurement')}
              value={unitOfMeasurementValue}
              autoMenuWidth
              onChange={handleMetricTypeChange}
              optionTitleProp="name"
              optionValueProp="value"
              options={unitOfMeasurementOptions}
              disabled={isAverageProgressPercent}
              MenuProps={{
                style: {
                  zIndex: '1500000',
                },
              }}
            />
            <div className="clear" />
          </div>
        </div>
        <div className="clear" />
      </div>
    </div>
  );
};

CascadePrimaryKrSection.defaultProps = {
  cascadeList: [],
};
